import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRealmApp } from '../RealmApp';

export default function useCustomerMutations() {
  const app = useRealmApp();
  return {
    addCustomer: useAddCustomer(app.currentUser.id),
    updateCustomer: useUpdateCustomer(app.currentUser.id),
    deleteCustomer: useDeleteCustomer(app.currentUser.id),
  };
}

const AddCustomerMutation = gql`
  mutation AddCustomer($customer: CustomerInsertInput!) {
    addedCustomer: insertOneCustomer(data: $customer) {
      _id
      address1
      address2
      city
      contactEmail
      contactName
      contactPhone
      country
      dateOfBirth
      email
      idNumber
      language
      lastName
      lastPurchaseDatetime
      name
      notes
      owner_id
      phone
      phone2
      qrData
      ref
      storeId
      tags
      webPage
      zip
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateCustomerMutation = gql`
  mutation UpdateCustomer(
    $customerId: ObjectId!
    $ownerId: String!
    $updates: CustomerUpdateInput!
  ) {
    updatedCustomer: updateOneCustomer(
      query: { _id: $customerId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      address1
      address2
      city
      contactEmail
      contactName
      contactPhone
      country
      dateOfBirth
      email
      idNumber
      language
      lastName
      lastPurchaseDatetime
      name
      notes
      owner_id
      phone
      phone2
      qrData
      ref
      storeId
      tags
      webPage
      zip
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteCustomerMutation = gql`
  mutation DeleteCustomer($customerId: ObjectId!, $ownerId: String!) {
    deletedCustomer: deleteOneCustomer(
      query: { _id: $customerId, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

const CustomerFieldsFragment = gql`
  fragment CustomerFields on Customer {
    _id
    address1
    address2
    city
    contactEmail
    contactName
    contactPhone
    country
    dateOfBirth
    email
    idNumber
    language
    lastName
    lastPurchaseDatetime
    name
    notes
    owner_id
    phone
    phone2
    qrData
    ref
    storeId
    tags
    webPage
    zip
    updateDatetime
    creationDatetime
  }
`;

function useAddCustomer(ownerId) {
  const [addCustomerMutation] = useMutation(AddCustomerMutation, {
    // Manually save added Customers into the Apollo cache so that Customer queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedCustomer } }) => {
      cache.modify({
        fields: {
          customers: (existingCustomers = []) => [
            ...existingCustomers,
            cache.writeFragment({
              data: addedCustomer,
              fragment: CustomerFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (customer) => {
    const {
      data: { addedCustomer },
    } = await addCustomerMutation({
      variables: {
        customer: {
          owner_id: ownerId,
          ...customer,
        },
      },
    });
    return addedCustomer;
  };
}

function useUpdateCustomer(ownerId) {
  const [updateCustomerMutation] = useMutation(UpdateCustomerMutation);
  //
  return async (customer, updates) => {
    const {
      data: { updatedCustomer },
    } = await updateCustomerMutation({
      variables: { customerId: customer._id, ownerId, updates },
    });
    return updatedCustomer;
  };
}

function useDeleteCustomer(ownerId) {
  const [deleteCustomerMutation] = useMutation(DeleteCustomerMutation, {
    update: (cache, { data: { deletedCustomer } }) => {
      cache.modify({
        fields: {
          customers: (existingCustomers = [], { readField }) =>
            existingCustomers.filter(
              (ref) => deletedCustomer._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (customer) => {
    const {
      data: { deletedCustomer },
    } = await deleteCustomerMutation({
      variables: { customerId: customer._id, ownerId },
    });
    return deletedCustomer;
  };
}
